//src/app/layout/dashboardLayout/BoxLayout.tsx
import { OptionsIcon } from 'assets/icons';
import OverlayWindow, { WindowSize } from 'Components/OverlayWindow';
import { useCreatePopup } from 'Components/Popups';
import Options from 'Components/Select/Options';
import { forwardRef, ReactNode, Ref } from 'react';

import { UserRoleEnum } from 'app/services/user';
import { partition } from 'lodash';
import { useLayoutEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'utils/AuthProvider';

export function BoxLayoutHeader({}: {
  title?: ReactNode;
  children?: ReactNode;
}) {
  return;
}

function Section({
  border,
  children,
}: {
  border?: string;
  children?: ReactNode;
}) {
  return (
    <div
      className={`${border} h-20 border-gray-lighter text-gray-dark font-semibold text-regular`}
    >
      {children}
    </div>
  );
}

export const ScrollContainer = forwardRef(
  (
    {
      className = '',
      children,
      style,
      id,
      overflow = 'overflow-y-auto',
      onClick,
      width = 'w-full flex-1',
      direction = 'flex-col',
    }: {
      className?: string;
      children: ReactNode;
      overflow?: 'overflow-y-auto' | 'overflow-y-scroll' | 'overflow-y-hidden';
      style?: any;
      id?: string;
      height?: string;
      width?: string;
      onClick?: React.MouseEventHandler<HTMLDivElement>;
      direction?: 'flex-col' | 'flex-row';
    },
    ref: Ref<HTMLDivElement>,
  ) => {
    return (
      <div
        ref={ref}
        id={id}
        onClick={onClick}
        className={`${className} ${width} flex ${direction} h-full ${overflow}`}
        style={style}
      >
        {children}
      </div>
    );
  },
);

export function DashboardContainerHeader({
  title,
  icon,
  subTitle,
  children,
  showBorder = true,
  border = 'border-b border-gray-800',
}: {
  border?: string;
  showBorder?: boolean;
  title: React.ReactNode;
  icon?: React.ReactNode;
  subTitle?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={`py-2 min-h-[64px] flex px-4 flex-wrap gap-2 ${
        showBorder ? border : ''
      }`}
    >
      <div className="mr-2 flex items-center gap-4 text-sm font-semibold">
        {icon}
        <div className="flex flex-1 w-full flex-col">
          {/* Changed from whitespace-nowrap to allow wrapping */}
          <div className="text-gray-dark font-semibold text-regular whitespace-normal break-words">
            {title}
          </div>
          <div className="text-gray-light font-semibold text-sm whitespace-normal">
            {subTitle}
          </div>
        </div>
      </div>

      <div className="flex flex-1 justify-end flex-wrap gap-2 items-center">
        {children}
      </div>
    </div>
  );
}

export function Container({
  children,
  className,
  style,
  bgColor = 'bg-white',
}: {
  children: ReactNode;
  className?: string;

  style?: any;
  bgColor?: string;
}) {
  return (
    <ScrollContainer
      style={style}
      className={`rounded-3xl md-max:rounded-t-xl ${bgColor} ${className}`}
    >
      {children}
    </ScrollContainer>
  );
}

export type Tab = {
  to: string;
  text: string;
  roles?: (
    | UserRoleEnum.CLIENT
    | UserRoleEnum.ACCOUNTANT
    | UserRoleEnum.ADMIN
    | undefined
  )[];
};
function isAllowed(
  tab: Tab,
  role?:
    | UserRoleEnum.CLIENT
    | UserRoleEnum.ACCOUNTANT
    | UserRoleEnum.ADMIN
    | undefined,
) {
  if (!role || !tab.roles) return true;

  return tab.roles?.includes(role);
}
export function Tabs({
  tabs,
  basePath,
  showActiveBorder,
}: {
  tabs: Tab[];
  basePath: string;
  showActiveBorder?: boolean;
}) {
  const { role } = useAuth();

  return (
    <>
      {tabs.map(
        (tab) =>
          isAllowed(tab, role) && (
            <NavLink
              key={tab.to}
              to={[basePath, tab.to].join('/')}
              data-to={tab.to}
              data-text={tab.text}
              className={({ isActive }) =>
                `${
                  isActive
                    ? `text-green ${
                        showActiveBorder ? 'active border-green border-b-4' : ''
                      }`
                    : 'text-gray-200 '
                } hover:text-green flex items-center`
              }
            >
              <div className="flex flex-col h-full whitespace-nowrap items-center font-semibold  text-sm">
                <span className="flex-1 flex items-center p-4">{tab.text}</span>
              </div>
            </NavLink>
          ),
      )}
    </>
  );
}

export function isVisible(parent: HTMLElement, child: HTMLElement) {
  return !(child.offsetLeft + child.clientWidth > parent.clientWidth);
}

export function ShowMoreTabsButton({
  tabs,

  basePath,
}: {
  tabs?: Tab[];
  basePath?: string;
}) {
  const Popup = useCreatePopup();
  const navigate = useNavigate();
  const locationParts = useLocation().pathname.split(`${basePath}/`);
  const currentLocation = locationParts[1] ?? '';

  return (
    <>
      <div
        className="flex items-center mx-4"
        onClick={Popup.toggle}
        ref={Popup.setAnchor}
      >
        <button className="p-2">
          <OptionsIcon className="w-4 h-4" />
        </button>
      </div>

      <Popup.Component>
        <OverlayWindow size={WindowSize.xs} paddingSize="">
          <Options
            value={tabs?.find((_) =>
              _.to.includes(currentLocation.split('/')[0]),
            )}
            itemId="to"
            optionItems={tabs!}
            onSelect={(t) => {
              navigate(t.to);
              Popup.close();
            }}
          >
            {(tab) => tab.text}
          </Options>
        </OverlayWindow>
      </Popup.Component>
    </>
  );
}

export function DashboardSubTabs({
  basePath,
  border = 'border-b border-gray-800',
  tabs,
  showActiveBorder = true,
  showBorder = true,
}: {
  basePath: string;
  border?: string;
  showBorder?: boolean;
  showActiveBorder?: boolean;
  tabs: Tab[];
}) {
  const isMobile = window.innerWidth < 768;
  const measurerRef = useRef<HTMLDivElement>(null);

  const [visibleTabs, setVisibleTabs] = useState<Tab[]>([]);
  const [invisibleTabs, setInvisibleTabs] = useState<Tab[]>([]);

  useLayoutEffect(() => {
    if (!measurerRef.current) return;

    const [visible, invisible] = partition(
      Array.from(measurerRef.current.children) as HTMLElement[],
      (tab) => measurerRef.current && isVisible(measurerRef.current, tab),
    );
    const tabFromElement = (e: HTMLElement) => ({
      to: e.getAttribute('data-to') || '#',
      text: e.getAttribute('data-text') || '',
    });
    setVisibleTabs(
      visible.filter((x) => !!x.getAttribute('data-to')).map(tabFromElement),
    );
    setInvisibleTabs(invisible.map(tabFromElement));
  }, [measurerRef.current?.clientWidth]);

  return (
    <div
      style={
        isMobile
          ? {
              //flexDirection: isMobile ? 'column' : 'row',
              width: '200px',
              //height: '100px',
              maxHeight: '64px',
              overflowX: isMobile ? 'auto' : 'unset',
              msOverflowStyle: 'none', // For Internet Explorer and Edge
              scrollbarWidth: 'none', // For Firefox
            }
          : { maxHeight: '64px' }
      }
      className={`flex flex-col h-full overflow-hidden ${
        showBorder ? border : ''
      }`}
    >
      <div ref={measurerRef} className="flex relative  h-0  overflow-hidden">
        <div className="w-10 mx-4" />
        <Tabs
          tabs={tabs}
          basePath={basePath}
          showActiveBorder={showActiveBorder}
        />
      </div>
      <div className="flex flex-1 h-full overflow-hidden ">
        {visibleTabs.length >= 2 && (
          <Tabs
            tabs={visibleTabs}
            basePath={basePath}
            showActiveBorder={showActiveBorder}
          />
        )}
        {invisibleTabs.length > 0 && (
          <ShowMoreTabsButton
            tabs={
              visibleTabs.length <= 2
                ? [...visibleTabs, ...invisibleTabs]
                : invisibleTabs
            }
            basePath={basePath}
          />
        )}
      </div>
    </div>
  );
}
